import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

import { Button } from './core/Button';
import { Colors, Mixins } from '../styles';
import { Icon as DSIcon } from '../lumen/components';
import { useBoundingClientRect } from '../utils/hooks';
import { CutoutOverlay } from './CutoutOverlay';

const wrapperStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  > :not(:last-child) {
    margin-right: 0.5rem;
  }

  button {
    align-self: center;
  }
`;

/**
 * InlineEditor component.
 *
 * @property {string} name - Used as the aria label for the form and in the
 *                           placeholder text in the input
 * @property {string} value - Initial value of the input
 * @property {function} onChange - Callback to be used when something changes.
 * @property {JSX.Element} [children] - Rendered inline, to the right of the pen
 *                                      icon when the editor is not in the
 *                                      active editing mode
 * @property {string} [blankValue] - Default value of the input, used when the
 *                                   value is blank. Should be supplied if
 *                                   `required` is false
 * @property {string} [placeholder] - Placeholder, passed to the HTML input
 * @property {string} [className] - Passed to the underlying HTML form rendered
 *                                  in editing mode
 * @property {boolean} [required] - Defaults to false. If true, blank values are
 *                                  not accepted. If false, `blankValue` should
 *                                  be supplied.
 * @property {boolean} [fillWidth] - Defaults to false. If true, editor takes
 *                                   the full width of its parent.
 * @property {boolean} [multiline] - Defaults to false. If true, a custom text
 *                                   area is rendering in editing mode instead
 *                                   of an input.
 * @property {string} [action] - Defaults to 'Edit'. Sets the verb in the ARIA label
 *                               of the pen icon.
 * @property {boolean} [ellipsify] - Defaults to True. Determines whether to
 *                                   shorten long values.
 * @property {boolean} [storybookEditing] - Defaults to false. Only for use in
 *                                          storybook. Displays the editor in
 *                                          editing mode.
 */
export default function InlineEditor({
  name,
  value,
  onChange,
  children,
  blankValue,
  placeholder,
  className,
  required = false,
  fillWidth = false,
  multiline = false,
  action = 'Edit',
  ellipsify = true,
  NameWrapper = props => <span {...props} />,
  storybookEditing = false // for use in storybook only
}) {
  const [editing, setEditing] = useState(false);
  const nameStyles = css([
    ellipsify && Mixins.ellipsify,
    css`
      min-width: 0;
      box-sizing: border-box;
      padding: 2px 8px;
      border: 1px solid transparent;
      border-radius: 8px;
      line-height: 1.3;
      height: calc(1.3em + 6px);
    `,
    fillWidth &&
      css`
        flex: 1;
      `,
    multiline &&
      css`
        height: auto;
        white-space: pre-wrap; /* soft wrap long lines */
        /* set padding and box-sizing to prevent a really annoying problem
         * where the project details section of the * upload page would get
         * 8px taller when you went to edit the description
         */
        padding-right:  0.5rem; /* don't smush top of text against border */
        box-sizing: border-box; /* account for padding in min-height */
        /* Don't let the text area take over the whole screen */
        max-height: 40vh;
        overflow-y: auto;
      `
  ]);
  const isEditing = editing || storybookEditing;
  const editorForm = isEditing && (
    <InlineEditorForm
      name={name}
      value={value}
      onChange={onChange}
      stopEditing={() => setEditing(false)}
      required={required}
      nameStyles={nameStyles}
      className={className}
      multiline={multiline}
      placeholder={placeholder}
      overlay={!multiline}
      {...storybookOverrides(storybookEditing, setEditing)}
    />
  );

  if (isEditing && multiline) {
    // Multiline editors swap in place so the textarea can grow freely
    return editorForm;
  }

  /* Single-line editors: the view row always stays in the layout flow and
     the edit form renders as an absolute overlay on top of it — the page
     can never shift when toggling edit mode. */
  return (
    <span
      css={[
        wrapperStyles,
        css`
          position: relative;
        `
      ]}
      className={className}
    >
      <NameWrapper
        css={[
          nameStyles,
          isEditing &&
            css`
              visibility: hidden;
            `
        ]}
      >
        {value || <em>{blankValue}</em>}
      </NameWrapper>
      <Button
        flavor="subtle"
        palette="gray"
        hiddenLabel={`${action} ${name}`}
        onClick={() => setEditing(true)}
        css={
          isEditing &&
          css`
            visibility: hidden;
          `
        }
      >
        <DSIcon name="pen-line" size={16} color={Colors.gray9} />
      </Button>
      {children}
      {editorForm}
    </span>
  );
}

InlineEditor.propTypes = {
  /** Used as the aria label for the form and in the placeholder text in the input. */
  name: PropTypes.string.isRequired,
  /** Initial value of the input. */
  value: PropTypes.string.isRequired,
  /** Callback to be used when something changes. */
  onChange: PropTypes.func.isRequired,
  /** Rendered inline, to the right of the pen icon when the editor is not in the active editing mode.*/
  children: PropTypes.node,
  /** Default value of the input, used when the value is blank. Should be supplied if `required` is false.*/
  blankValue: PropTypes.string,
  /** Placeholder, passed to the HTML input.*/
  placeholder: PropTypes.string,
  /** Passed to the underlying HTML form rendered in editing mode.*/
  className: PropTypes.string,
  /** Defaults to false. If true, blank values are not accepted. If false, `blankValue` should be supplied.*/
  required: PropTypes.bool,
  /** Defaults to false. If true, editor takes the full width of its parent.*/
  fillWidth: PropTypes.bool,
  /** Defaults to false. If true, a custom text area is rendering in editing mode instead of an input.*/
  multiline: PropTypes.bool,
  /** Defaults to 'Edit'. Sets the verb in the ARIA label of the pen icon.*/
  action: PropTypes.string,
  /** Defaults to True. Determines whether to shorten long values.*/
  ellipsify: PropTypes.bool,
  /** Defaults to false. Only for use in storybook. Displays the editor in editing mode. */
  storybookEditing: PropTypes.bool,
  /** Element that wraps the name. Defaults to a span. */
  NameWrapper: PropTypes.elementType
};

const storybookOverrides = (storybookEditing, setEditing) =>
  storybookEditing && {
    stopEditing: () => setEditing(true)
  };

/**
 * The form rendered by the inline editor when in editing mode.
 */
function InlineEditorForm({
  name,
  value,
  onChange,
  stopEditing,
  required,
  nameStyles,
  className,
  multiline,
  placeholder,
  overlay = false
}) {
  const inputRef = useRef();
  const formRef = useRef();
  const [currentValue, setCurrentValue] = useState(value);
  const trimmedCurrentValue = currentValue.trim();
  useLayoutEffect(() => {
    inputRef.current.select();
  }, []);
  const FormControl = multiline ? AutoGrowingTextArea : 'input';
  const submitForm = event => {
    event.preventDefault();
    if (trimmedCurrentValue !== '' || !required) {
      onChange(trimmedCurrentValue);
    }
    stopEditing();
  };
  return (
    <>
      <form
        ref={formRef}
        css={[
          wrapperStyles,
          overlay &&
            css`
              position: absolute;
              inset: 0;
            `
        ]}
        className={className}
        onBlur={event => {
          if (!formRef.current.contains(event.relatedTarget)) {
            submitForm(event);
          }
        }}
        onSubmit={submitForm}
      >
        <FormControl
          ref={inputRef}
          aria-label={_.upperFirst(name)}
          value={currentValue}
          placeholder={placeholder ?? `Type a new ${name}`}
          onChange={event => setCurrentValue(event.target.value)}
          onKeyDown={event => {
            // Escape discards the edit; Enter submits (form), blur saves
            if (event.key === 'Escape') {
              stopEditing();
            }
          }}
          autoFocus
          css={theme =>
            css([
              nameStyles,
              /* "&&" out-ranks legacy global input[type] styles; the control
                 inherits the surrounding typography so the text doesn't jump
                 when entering edit mode. */
              css`
                && {
                  margin: 0;
                  /* Browsers ignore line-height on <input>, so give it the
                     exact height the view-mode span computes:
                     1.3em text + 4px padding + 2px borders */
                  height: ${multiline ? 'auto' : 'calc(1.3em + 6px)'};
                  /* fill the row while editing (the view text hugs its
                     content so the pen icon stays next to it) */
                  flex: 1;
                  width: 100%;
                  min-width: 0;
                  font: inherit;
                  line-height: 1.3;
                  color: inherit;
                  background: ${theme.color.bg.default};
                  border: 1px solid ${theme.color.slate[300]};
                  border-radius: 8px;
                  box-shadow: none;
                  outline: none;
                }

                &&:focus {
                  border-color: ${theme.color.primary.base};
                  box-shadow: 0 0 0 3px ${theme.color.blue[100]};
                }
              `
            ])
          }
        />
      </form>
      <ClickBlocker formRef={formRef} onClose={stopEditing} />
    </>
  );
}

const AutoGrowingTextArea = forwardRef(({ value, ...props }, ref) => {
  const [height, setHeight] = useState(1);
  useLayoutEffect(() => {
    setHeight(ref.current.scrollHeight);
  }, [value]);
  return (
    <textarea
      css={css`
        height: ${height}px;
      `}
      ref={ref}
      value={value}
      {...props}
    />
  );
});

// This is a clear div that covers the whole page *except* for the InlineEditor.
// Since we submit on a blur effect, we want to avoid a situation in which the
// cause of the blur also triggers a different event that interacts weirdly with
// the submit (e.g. clicking on a delete button while editing a name).
const ClickBlocker = ({ formRef, onClose }) => {
  const rect = useBoundingClientRect(formRef);
  return createPortal(
    <CutoutOverlay rect={rect} onClick={onClose} />,
    document.body
  );
};
